import { Sparkles } from "lucide-react";

/**
 * Shared "AI is working" indicator used in the Social Post Generator,
 * Speech Writer, and Campaign Advisor. Three staggered bouncing dots +
 * a short label. Designed to read clearly as "the tool is thinking,
 * not frozen."
 *
 * Props:
 *   label   — text shown next to the dots. Defaults to "Thinking…".
 *   variant — "block" (default) renders inside a card; "inline" is
 *             for embedding inside a chat thread or a button row.
 *   testid  — data-testid for the wrapper.
 */
export function ThinkingIndicator({
  label = "Thinking…",
  variant = "block",
  testid = "thinking-indicator",
}) {
  const dots = (
    <span className="inline-flex items-center gap-1" aria-hidden="true">
      <span className="h-2 w-2 rounded-full bg-flag-red animate-bounce [animation-delay:-0.32s]" />
      <span className="h-2 w-2 rounded-full bg-flag-red animate-bounce [animation-delay:-0.16s]" />
      <span className="h-2 w-2 rounded-full bg-flag-red animate-bounce" />
    </span>
  );

  if (variant === "inline") {
    return (
      <div
        className="inline-flex items-center gap-2 text-slate-500 italic"
        data-testid={testid}
        role="status"
        aria-live="polite"
      >
        <Sparkles className="h-3.5 w-3.5 animate-pulse text-flag-red" />
        <span>{label}</span>
        {dots}
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-8 flex flex-col items-center gap-3 text-slate-600 animate-fade-up"
      data-testid={testid}
      role="status"
      aria-live="polite"
    >
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-flag-red/10 text-flag-red">
        <Sparkles className="h-5 w-5 animate-pulse" />
      </div>
      <div className="inline-flex items-center gap-3">
        <span className="font-display font-bold text-flag-navy">{label}</span>
        {dots}
      </div>
      <p className="text-xs text-slate-500">Claude is drafting in your voice — usually under 10 seconds.</p>
    </div>
  );
}

export default ThinkingIndicator;
